import React, { createContext, useState, useContext, useEffect } from "react";
import { supabase } from "../lib/supabase";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);

  /** PROFILE HELPERS */
  const ensureProfile = async (supabaseUser) => {
    if (!supabaseUser) return;
    try {
      await supabase.from("profiles").upsert(
        {
          id: supabaseUser.id,
          email: supabaseUser.email,
          name: supabaseUser.user_metadata?.full_name || "",
          role: supabaseUser.user_metadata?.role || null,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "id" }
      );
    } catch (err) {
      console.warn("Profile upsert failed:", err.message);
    }
  };

  const loadProfile = async (supabaseUser) => {
    if (!supabaseUser) return;
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", supabaseUser.id)
        .maybeSingle();
      if (error && error.code !== "PGRST116") throw error;
      if (data) {
        setProfile(data);
        if (data.role) setRole(data.role);
      }
    } catch (err) {
      console.warn("Profile load failed:", err.message);
    }
  };

  /** INIT + AUTH EVENTS */
  useEffect(() => {
    let listener;

    const initAuth = async () => {
      try {
        const {
          data: { session: currentSession },
        } = await supabase.auth.getSession();
        const currentUser = currentSession?.user ?? null;

        setSession(currentSession);
        setUser(currentUser);
        setRole(currentUser?.user_metadata?.role || null);

        if (currentUser) {
          await ensureProfile(currentUser);
          await loadProfile(currentUser);
        }

        // Listen for auth changes (multi-tab support)
        listener = supabase.auth.onAuthStateChange(
          async (event, newSession) => {
            const newUser = newSession?.user ?? null;

            setSession(newSession);
            setUser(newUser);
            setRole(newUser?.user_metadata?.role || null);

            if (event === "SIGNED_IN" && newUser) {
              await ensureProfile(newUser);
              await loadProfile(newUser);
            }

            if (event === "SIGNED_OUT") {
              clearAuthState();
            }
          }
        ).subscription;
      } catch (err) {
        console.error("Auth init error:", err);
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    // Multi-tab logout fix: listen to localStorage changes
    const handleStorageChange = (event) => {
      if (event.key === "supabase.auth.token") {
        supabase.auth.getSession().then(({ data: { session: newSession } }) => {
          const newUser = newSession?.user ?? null;
          setSession(newSession);
          setUser(newUser);
          setRole(newUser?.user_metadata?.role || null);
          if (!newUser) clearAuthState();
        });
      }
    };
    window.addEventListener("storage", handleStorageChange);

    return () => {
      if (listener) listener.unsubscribe();
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  /** Clear all auth states */
  const clearAuthState = () => {
    setUser(null);
    setSession(null);
    setProfile(null);
    setRole(null);
  };

  /** AUTH FUNCTIONS */
  const signOut = async () => {
    try {
      clearAuthState();
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      console.log("Signed out successfully!");
      return { error: null };
    } catch (err) {
      console.error("Sign out error:", err);
      return { error: err };
    }
  };

  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
      if (data.user) {
        await ensureProfile(data.user);
        await loadProfile(data.user);
        setUser(data.user);
        setRole(data.user.user_metadata?.role || null);
      }
      return { user: data.user, error: null };
    } catch (err) {
      console.error("Sign in error:", err);
      return { user: null, error: err };
    }
  };

  const signUp = async (email, password, metadata = {}) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: metadata },
      });
      if (error) throw error;
      if (data.user) {
        await ensureProfile(data.user);
        await loadProfile(data.user);
        setUser(data.user);
        setRole(metadata.role || null);
      }
      return { user: data.user, error: null };
    } catch (err) {
      console.error("Sign up error:", err);
      return { user: null, error: err };
    }
  };

  const googleSignIn = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
    });
    if (error) throw error;
  };

  const value = {
    user,
    session,
    profile,
    role,
    loading,
    signUp,
    signIn,
    googleSignIn,
    signOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
